import { useCallback } from "react";
import { postIlfDlf } from "@/modules/Quote/api/ratingApi.js";
import { ensureSpecialityId } from "@/modules/Quote/api/specialityApi.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import { useStore } from "@/shared/store/useStore.js";
import { QuotesRequest } from "@/shared/dtos/index.js";
import { toError } from "@/shared/utils/misc.js";

/** Subscribe to the cached QuoteFormData response. */
export function useIlfDlfResponse() {
  return useStore(ilfDlfStore, (s) => s.ilfDlfResponse);
}

/** Subscribe to the ILF/DLF fetch error. */
export function useIlfDlfError() {
  return useStore(ilfDlfStore, (s) => s.ilfDlfError);
}

/** Subscribe to the ILF/DLF loading flag. */
export function useIlfDlfLoading() {
  return useStore(ilfDlfStore, (s) => s.ilfDlfLoading);
}

/**
 * Returns a `fetchIlfDlf(body)` function that:
 *   - returns the cached response if the same (zip|spec|date) tuple was
 *     fetched before in this tab
 *   - otherwise flips the loading flag, calls POST /auth/quotedata, stores
 *     the response in `ilfDlfStore`, and returns the QuoteFormData
 *   - on failure: stores the error AND throws so the caller can react
 */
export function useIlfDlfFetcher() {
  return useCallback(async (body) => {
    // Always source the speciality id from the SP-master fetch (cached
    // after the first hit on /auth/speciality/SP_203). Any caller-provided
    // specialtiesid is overridden so /auth/quotedata never goes out with a
    // stale or guessed id.
    const masterId = await ensureSpecialityId();
    const reqBody = { ...body, ...(masterId != null ? { specialtiesMasterId: masterId } : {}) };
    const key = ilfDlfStore.cacheKey(reqBody);
    const cached = ilfDlfStore.getIlfDlf(key);
    if (cached) {
      ilfDlfStore.setIlfDlf(key, cached);
      ilfDlfStore.ilfDlfError = null;
      return cached;
    }
    ilfDlfStore.ilfDlfLoading = true;
    ilfDlfStore.ilfDlfError = null;
    try {
      const data = await postIlfDlf(new QuotesRequest(reqBody));
      ilfDlfStore.setIlfDlf(key, data);
      return data;
    } catch (e) {
      ilfDlfStore.ilfDlfError = toError(e);
      throw e;
    } finally {
      ilfDlfStore.ilfDlfLoading = false;
    }
  }, []);
}

export const resetIlfDlfResponse = () => ilfDlfStore.clearResponse();
export const resetIlfDlfError = () => { ilfDlfStore.ilfDlfError = null; };
